"use client"

import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * UserAvatar - Robust avatar component that never shows broken images
 *
 * Features:
 * - Gradient + initials fallback (never shows broken image icon)
 * - Dual-layer cache (module + localStorage) prevents placeholder flash
 * - Persists across page refreshes via localStorage
 * - Zero-flash for cached images using useLayoutEffect + direct DOM manipulation
 * - Smooth fade-in only for uncached images
 * - Instagram CDN proxy support
 */

// ============================================
// IMAGE CACHE SYSTEM
// Dual-layer: module-level Set + localStorage persistence
// ============================================
const STORAGE_KEY = "outfame_avatar_cache"
const MAX_CACHED_URLS = 50 // Prevent localStorage bloat

// Module-level cache - survives component unmounts within session
const loadedImageCache = new Set<string>()

// Hydrate module cache from localStorage on init (runs once on module load)
function hydrateFromStorage(): void {
    if (typeof window === "undefined") return
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            const urls = JSON.parse(stored) as string[]
            urls.forEach((url) => loadedImageCache.add(url))
        }
    } catch {
        // localStorage unavailable or corrupted, start fresh
    }
}

// Persist module cache to localStorage
function persistToStorage(): void {
    if (typeof window === "undefined") return
    try {
        // Convert to array, keep only recent entries
        const urls = Array.from(loadedImageCache).slice(-MAX_CACHED_URLS)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(urls))
    } catch {
        // localStorage unavailable
    }
}

// Run hydration immediately when module loads (client-side only)
if (typeof window !== "undefined") {
    hydrateFromStorage()
}

// Add URL to cache and persist
function addToCache(url: string): void {
    if (!url || loadedImageCache.has(url)) return
    loadedImageCache.add(url)
    persistToStorage()
}

// Check if URL is in cache
function isInCache(url: string | null | undefined): boolean {
    return url ? loadedImageCache.has(url) : false
}

// Check if image is in browser cache (synchronous check)
function isInBrowserCache(url: string | null | undefined): boolean {
    if (!url || typeof window === "undefined") return false
    const img = new window.Image()
    img.src = url
    return img.complete && img.naturalWidth > 0
}

// Preload an image URL and add to cache when loaded
export function preloadAvatar(url: string | null | undefined): void {
    if (!url || loadedImageCache.has(url)) return
    const img = new window.Image()
    img.onload = () => addToCache(url)
    img.src = url
}

// Check if an image URL is already cached (public API)
export function isAvatarCached(url: string | null | undefined): boolean {
    return isInCache(url)
}

interface UserAvatarProps {
    /** Image source URL */
    src?: string | null
    /** Alt text and source for initials fallback */
    alt?: string
    /** Username to display as @username or extract initial from */
    username?: string
    /** Size preset or custom className for dimensions */
    size?: "xs" | "sm" | "md" | "lg" | "xl" | number
    /** Additional className */
    className?: string
    /** Border radius style */
    rounded?: "full" | "xl" | "lg"
    /** Show ring/border */
    ring?: boolean
}

// Size presets in pixels
const sizeMap = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64
}

// Tailwind size classes
const sizeClasses = {
    xs: "h-6 w-6 text-[10px]",
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg"
}

// Rounded classes
const roundedClasses = {
    full: "rounded-full",
    xl: "rounded-xl",
    lg: "rounded-lg"
}

/**
 * Generate a consistent color based on string (username/alt)
 * Returns a gradient that's visually distinct per user
 */
function getGradientFromString(str: string): string {
    const gradients = ["from-violet-500 to-purple-600", "from-blue-500 to-cyan-500", "from-emerald-500 to-teal-500", "from-orange-500 to-red-500", "from-pink-500 to-rose-500", "from-indigo-500 to-blue-600", "from-amber-500 to-orange-500", "from-cyan-500 to-blue-500"]

    // Simple hash from string
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }

    return gradients[Math.abs(hash) % gradients.length]
}

/**
 * Get initial(s) from username or alt text
 */
function getInitials(username?: string, alt?: string): string {
    const source = username || alt || ""
    if (!source) return "?"

    // If it's a username starting with @, remove it
    const clean = source.replace(/^@/, "")

    // Return first character uppercase
    return clean.charAt(0).toUpperCase()
}

export function UserAvatar({ src, alt, username, size = "md", className, rounded = "full", ring = true }: UserAvatarProps) {
    const imageRef = React.useRef<HTMLImageElement>(null)
    const [imageError, setImageError] = React.useState(false)
    
    // Check if cached at render time (before effects run)
    // This determines whether we apply transition class
    const isCachedAtRender = React.useMemo(() => {
        return isInCache(src) || isInBrowserCache(src)
    }, [src])

    // useLayoutEffect runs synchronously before browser paint
    // If image is already loaded (browser cache), mark as loaded immediately
    React.useLayoutEffect(() => {
        const img = imageRef.current
        if (!img || !src) return
        
        // Check if image is already complete (browser cached)
        if (img.complete && img.naturalWidth > 0) {
            img.dataset.loaded = "true"
            addToCache(src)
        }
    }, [src])

    // Reset error state when src changes
    React.useEffect(() => {
        setImageError(false)
    }, [src])

    // Handle successful image load
    const handleLoad = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget
        img.dataset.loaded = "true"
        if (src) addToCache(src)
    }, [src])

    // Handle image error
    const handleError = React.useCallback(() => {
        setImageError(true)
    }, [])

    const sizeClass = typeof size === "number" ? "" : sizeClasses[size]
    const roundedClass = roundedClasses[rounded]
    const gradient = getGradientFromString(username || alt || "default")
    const initials = getInitials(username, alt)

    const hasValidSrc = src && src.length > 0 && !imageError

    return (
        <div className={cn("relative flex shrink-0 overflow-hidden", sizeClass, roundedClass, ring && "ring-2 ring-white", className)} style={typeof size === "number" ? { width: size, height: size } : undefined}>
            {/* Gradient fallback - always rendered as base layer */}
            <div className={cn("absolute inset-0 flex items-center justify-center bg-gradient-to-br font-semibold text-white", gradient)}>{initials}</div>

            {/* Image layer - uses data attribute for load state to avoid React re-renders
                - Cached images: start visible (data-loaded="true" set in useLayoutEffect before paint)
                - Uncached images: start hidden, fade in on load
                - Transition only applied to uncached images to prevent any flash */}
            {hasValidSrc && (
                <img
                    ref={imageRef}
                    src={src}
                    alt={alt || username || "User avatar"}
                    className={cn(
                        "img-outline absolute inset-0 h-full w-full object-cover", roundedClass,
                        // Cached images: no transition, just instant opacity
                        // Uncached images: transition for smooth fade-in
                        isCachedAtRender 
                            ? "opacity-100" 
                            : "opacity-0 transition-opacity duration-200 ease-out data-[loaded=true]:opacity-100"
                    )}
                    data-loaded={isCachedAtRender ? "true" : undefined}
                    onLoad={handleLoad}
                    onError={handleError}
                    loading="eager"
                    decoding="sync"
                />
            )}
        </div>
    )
}
